import { useEffect } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { signOutApi } from '../services/auth-api'
import {
  clearAllCheckoutIds,
  clearCheckoutUser,
  clearReportedLogin,
} from '../../lib/checkout-session'
import { shutdownFeaturebase } from '../../lib/featurebase-session'
import { resetPostHog } from '../../lib/posthog'

const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID || ''

export function LogoutPage() {
  const { signOut } = useAuth()

  // Preserve reason param (e.g. ?reason=session_expired) so login page can show a message
  const reason = new URLSearchParams(window.location.search).get('reason')

  useEffect(() => {
    const run = async () => {
      // Mark as intentional logout so handleSessionExpired doesn't
      // overwrite the reason with 'session_expired'
      sessionStorage.setItem('intentional_logout', '1')

      if (reason) {
        sessionStorage.setItem('logout_reason', reason)
      } else {
        sessionStorage.setItem('logout_reason', 'signed_out')
      }

      // For forced logouts (session expired, no tenant, etc.), skip the
      // backend revocation — the session is already gone on WorkOS's side
      // and calling signOutApi() would re-populate localStorage via
      // resolveAccessToken(), creating an infinite loop.
      if (!reason) {
        try {
          await signOutApi()
        } catch {
          // Server-side revocation failed; continue with client-side sign-out.
        }
      }

      // Clear tokens — always do this before AuthKit signOut to prevent
      // resolveAccessToken() from writing them back.
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')

      // Drop the analytics checkout ids here rather than relying on a React
      // cleanup: this page stays mounted with the user still resolved until
      // the browser is redirected away, so an unmount-time check would not
      // run (or would still see a logged-in user). Engineers share devices,
      // and a surviving id would attach the next user's checkout to this
      // user's funnel.
      clearAllCheckoutIds()
      // Forget the reported login too, so signing back in — as this user or
      // another on a shared device — reports a new session.
      clearReportedLogin()
      // Reset PostHog identity here for the same reason as the checkout ids
      // above: this page redirects the browser, so a React-cleanup reset would
      // not run. Without it the next engineer on a shared device would be
      // identified into this user's PostHog session and person.
      resetPostHog()
      // Same for Featurebase: sign this engineer out of its messenger so the
      // next engineer on a shared device can't see their conversations.
      shutdownFeaturebase()
      // Forget the last-user marker too. PostHog is already reset here, so the
      // next sign-in must NOT be seen as a user switch — otherwise App.tsx would
      // reset a second time and discard the session the new sign-in built during
      // login/callback. Left set only when logout is skipped, so that backstop
      // still fires then.
      clearCheckoutUser()

      // Try AuthKit's signOut first — clears local state + redirects to WorkOS logout.
      try {
        signOut({
          navigate: true,
          returnTo: `${window.location.origin}/auth/login`,
        })
        return
      } catch {
        // AuthKit may throw if no active local session exists.
        // Fall through to direct WorkOS logout URL below.
      }

      // AuthKit signOut failed — redirect to the WorkOS logout endpoint directly
      // to ensure the WorkOS session cookies are cleared. Without this, the user
      // appears logged out locally but WorkOS auto-logs them back in.
      const returnTo = encodeURIComponent(`${window.location.origin}/auth/login`)
      window.location.href =
        `https://api.workos.com/user_management/sessions/logout?client_id=${WORKOS_CLIENT_ID}&return_to=${returnTo}`
    }

    run()
  }, [signOut, reason])

  return <LoadingSpinner message="Signing out..." />
}
